import { Injectable } from "@angular/core";
import { AuthData } from "./auth.service";
import { environment } from "src/environments/environment";
import * as crypto from 'crypto-js';

const authorizationDataKey: string = 'authorizationData';
const machineIdKey: string = 'machineId';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  getItem<T>(key: string): T {
    const savedDataJson = localStorage.getItem(key);
    if (!savedDataJson) {
      return <T>undefined;
    }
    return <T>JSON.parse(savedDataJson);
  }

  hasItem<T>(key: string): boolean {
    const savedDataJson = localStorage.getItem(key);
    return savedDataJson != null;
  }

  removeItem<T>(key: string) {
    localStorage.removeItem(key);
  }

  setItem<T>(key: string, item: T | null | undefined) {
    if (item == null || item == undefined) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(item));
  }

  set authorizationData(authData: AuthData | null) {
    if (!authData) {
      this.setItem<AuthData>(authorizationDataKey, null);
      return;
    }
    const encryptedInfo = crypto.AES.encrypt(JSON.stringify(authData), environment.signatureKey).toString();
    this.setItem<string>(authorizationDataKey, encryptedInfo);
  }

  get authorizationData(): AuthData | null {
    const encryptedAuthInfo = this.getItem<string>(authorizationDataKey)
    if (!encryptedAuthInfo) {
      return null;
    }
    //backward compatibility fix: previously we were storing object in local storage now we store encrypted string
    if (typeof (encryptedAuthInfo) === 'object') {
      this.authorizationData = null;
      return null;
    }
    try {
      const decryptedBytes = crypto.AES.decrypt(encryptedAuthInfo, environment.signatureKey);
      const decrypted = decryptedBytes.toString(crypto.enc.Utf8);
      if (!decrypted) {
        return null;
      }
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }

  set authorizationDataEncrypted(encryptedAuthInfo: string) {
    this.setItem<string>(authorizationDataKey, encryptedAuthInfo);
  }

  get authorizationDataEncrypted(): string {
    return this.getItem<string>(authorizationDataKey);
  }

  get machineId(): string {
    return this.getItem<string>(machineIdKey);
  }

  set machineId(machineId: string | null) {
    this.setItem<string>(machineIdKey, machineId);
  }

  /**
   * Wipe browser session + local storage on sign-out.
   * Snapshot auth tokens before calling if they are needed for cross-app logout redirect.
   */
  clearAllClientStorage(): void {
    try {
      sessionStorage.clear();
    } catch {
      // ignore quota / private mode errors
    }
    try {
      localStorage.clear();
    } catch {
      // ignore quota / private mode errors
    }
  }

}
